import { yupResolver } from '@hookform/resolvers/yup'
import { Collapse, Form, Spin } from 'antd'
import { InputDateField, InputTextField, InputTextAreaField } from 'components'
import dayjs from 'dayjs'
import { Trash } from 'phosphor-react'
import React, { useEffect, useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import styled from 'styled-components'
import { CompetitionTabScreen, DateUtility } from 'utility'
import * as yup from 'yup'
import { RowWrapper } from './CompetitionsAddEdit'

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 22px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  background: #ffe9e9;
  color: #ff5b5b;
  &:disabled {
    cursor: not-allowed;
  }
  &:hover {
    opacity: 0.8;
  }
`

const AddRoundButton = styled.button`
  border: none;
  padding: 10px 22px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  background: #e9efff;
  color: #0045ff;
  &:disabled {
    cursor: not-allowed;
  }
  &:hover {
    opacity: 0.8;
  }
`

const SaveButton = styled.button`
  padding: 10px 22px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  background: linear-gradient(90deg, #0045ff 0%, #006bff 100%);
  color: white;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  &:hover {
    opacity: 0.9;
  }
`

const StyledCollapse = styled(Collapse)`
  .ant-collapse-content-box {
    padding: 20px;
    border: none;
    border-radius: 8px;
  }

  .ant-collapse-header {
    background: #f5f7ff;
    font-weight: bold;
    border: none;
    font-size: 16px;
    color: #333;
  }
`
const RoundsBox = styled.div`
  margin-bottom: 20px;
  border: none;
  padding: 10px;
  border-radius: 8px;
`

const CompetitionsRoundsSchema = yup.object().shape({
  rounds: yup.array().of(
    yup.object().shape({
      name: yup
        .string()
        .trim('Spaces not allowed')
        .strict()
        .required('Name is required'),
      submission_deadline: yup
        .string()
        .required('Submission deadline is required'),
      voting_deadline: yup.string().required('Voting deadline is required'),
      no_of_winners: yup
        .number()
        .typeError('Winners is required')
        .required('Winners is required'),
      start_date: yup.string().required('Start date is required'),
      end_date: yup.string().required('End date is required'),
      // result_announcement: yup
      //   .string()
      //   .required('Result announcement is required'),
      instruction: yup
        .string()
        .trim('Spaces not allowed')
        .strict()
        .required('Instruction is required'),
    }),
  ),
})

export const CompetitionsRoundsTabAddEdit = ({ data, onNext, loading, disable }) => {
  const [activeKeys, setActiveKeys] = useState(['0'])
  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(CompetitionsRoundsSchema),
    defaultValues: {
      rounds: [
        {
          name: '',
          submission_deadline: '',
          voting_deadline: '',
          result_announcement: '',
          instruction: '',
          no_of_winners: '',
          start_date: '',
          end_date: '',
        },
      ],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'rounds',
  })

  useEffect(() => {
    if (data && data?.rounds?.length) {
      // Convert dates to dayjs and reset the form with the provided data
      const formattedRounds = data.rounds.map(ele => ({
        ...ele,
        submission_deadline: dayjs(ele.submission_deadline),
        voting_deadline: dayjs(ele.voting_deadline),
        result_announcement: dayjs(ele.result_announcement),
        end_date: dayjs(ele.end_date),
        start_date: dayjs(ele.start_date),
      }))
      reset({ rounds: formattedRounds })
    }
  }, [data])

  const submit = formData => {
    const updatedFormData = {
      ...formData,
      rounds: formData.rounds.map(round => ({
        ...round,
        result_announcement: formData.end_date,
      })),
    };
    onNext(CompetitionTabScreen.Rounds, updatedFormData)
  }
  const handleAddRound = () => {
    const newIndex = fields.length.toString() // New panel index
    append({
      name: '',
      submission_deadline: '',
      voting_deadline: '',
      result_announcement: '',
      instruction: '',
      no_of_winners: '',
      start_date: '',
      end_date: '',
    })
    setActiveKeys(prevKeys => [...prevKeys, newIndex]) // Auto-open new panel
  }

  const handleRemoveRound = index => {
    remove(index)
    setActiveKeys(prevKeys => prevKeys.filter(key => key !== index.toString())) // Close the removed panel
  }

  return (
    <Form disabled={disable} onFinish={handleSubmit(submit)}>
      <StyledCollapse
        activeKey={activeKeys}
        onChange={keys => setActiveKeys(keys)}
      >
        {fields.map((item, index) => {
          const roundStartDate = watch(`rounds[${index}].start_date`)
          const roundEndDate = watch(`rounds[${index}].end_date`)

          return (
            <Collapse.Panel
              header={`Round ${index + 1}`}
              key={index.toString()}
              extra={
                <Trash
                  onClick={event => {
                    event.stopPropagation()
                    remove(index)
                  }}
                  style={{ color: 'red', cursor: 'pointer' }}
                />
              }
            >
              <RoundsBox key={item.id}>
                <RowWrapper>
                  <InputTextField
                    required
                    name={`rounds[${index}].name`}
                    control={control}
                    errors={errors?.rounds?.[index]?.name?.message}
                    label="Name"
                    placeholder="Name"
                  />
                  <InputTextField
                    required
                    name={`rounds[${index}].no_of_winners`}
                    control={control}
                    type="number"
                    errors={errors?.rounds?.[index]?.no_of_winners?.message}
                    label="No of winners"
                    placeholder="No of winners"
                  />
                </RowWrapper>
                <RowWrapper>
                  <InputDateField
                    required
                    name={`rounds[${index}].start_date`}
                    control={control}
                    errors={errors?.rounds?.[index]?.start_date?.message}
                    label="Start Date"
                    placeholder="Start Date Of Round"
                    disabledDate={current => {
                      return current && current < DateUtility.startDay(new Date());
                    }}
                  />
                  <InputDateField
                    required
                    name={`rounds[${index}].end_date`}
                    control={control}
                    errors={errors?.rounds?.[index]?.end_date?.message}
                    label="End Date"
                    disabled={!roundStartDate || disable}
                    placeholder="End Date Of Round"
                    disabledDate={current =>
                      current && current < DateUtility.endDay(roundStartDate)
                    }
                  />
                </RowWrapper>
                <RowWrapper>
                  <InputDateField
                    required
                    name={`rounds[${index}].submission_deadline`}
                    control={control}
                    errors={
                      errors?.rounds?.[index]?.submission_deadline?.message
                    }
                    label="Video Submission Deadline"
                    placeholder="Video Submission Deadline"
                    disabled={!roundStartDate || disable}
                    disabledDate={current => {
                      if (!current) return false;
                      const start = DateUtility.startDay(roundStartDate);
                      const end = DateUtility.endDay(roundEndDate);
                      return current < start || current > end;
                    }}
                  />
                  <InputDateField
                    required
                    name={`rounds[${index}].voting_deadline`}
                    control={control}
                    errors={errors?.rounds?.[index]?.voting_deadline?.message}
                    label="Voting Deadline Date"
                    placeholder="Voting Deadline Date"
                    disabled={!roundStartDate || disable}
                    disabledDate={current => {
                      if (!current) return false;
                      const start = DateUtility.startDay(roundStartDate);
                      const end = DateUtility.endDay(roundEndDate);
                      return current < start || current > end;
                    }}
                  />
                </RowWrapper>
                <InputDateField
                  required
                  name={`rounds[${index}].end_date`}
                  control={control}
                  errors={errors?.rounds?.[index]?.end_date?.message}
                  label="Result Announcement"
                  placeholder="Select Result Announcement Date"
                  disabled
                  disabledDate={(current) => {
                    return current && !current.isSame(roundEndDate, 'day');
                  }}
                />
                <InputTextAreaField
                  required
                  name={`rounds[${index}].instruction`}
                  control={control}
                  errors={errors?.rounds?.[index]?.instruction?.message}
                  label="Instruction"
                  placeholder="Instruction"
                />
                <div className="d-flex justify-content-center align-items-center">
                  <DeleteButton
                    type="button"
                    disabled={disable || loading}
                    onClick={() => handleRemoveRound(index)}
                  >
                    <Trash /> Delete
                  </DeleteButton>
                </div>
              </RoundsBox>
            </Collapse.Panel>
          )
        })}
      </StyledCollapse>
      <div className="my-3 d-flex justify-content-center align-items-center">
        <AddRoundButton disabled={disable || loading} onClick={() => handleAddRound()}>
          Add New Round
        </AddRoundButton>

        <SaveButton disabled={disable || loading} className="mx-2" htmlType="submit">
          {loading && <Spin className="mr-2" />} Save
        </SaveButton>
      </div>
    </Form>
  )
}
